import { DateTime, parseToDateTime } from '@calendly/datetime';
import { useFormContext } from '@calendly/form';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { IconButton } from '@calendly/ui/components/icon-button';
import { AddIcon, CloseIcon, EditPencilIcon } from '@calendly/ui/components/icons';
import { BodyText } from '@calendly/ui/components/typography';
import classNames from 'classnames';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTrack } from 'hooks/analytics_events';
import { TRACKING_EVENT_NAMES } from 'hooks/analytics_events/constants';
import { CouponModal } from './create_coupon_modal';
import { resolvePaymentProviderForServer } from './payment_provider_utils';
import { useUpdateEventTypeMutation } from '../../../../api';

// Utility functions for consistent date handling
const formatExpirationDate = dateString => {
  const date = parseToDateTime(dateString);
  if (date?.isValid) {
    return date.toFormat('MMM d');
  }
  return null;
};
const parseExpirationDateTime = (dateString, timeString) => {
  const dateTime = DateTime.fromISO(`${dateString}T${timeString}`);
  return dateTime.isValid ? dateTime : null;
};
const toCamelCase = str => str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

/**
 * Used for analytics when updating an existing coupon.
 * Returns an array representation of an object (fields and values) with the field names
 * filtered and normalized to the expected analytics reporting format, coercing values
 * to strings. If the new value is the same as the previous value, the field is not included
 * in the array. Some fields are excluded or renamed for analytics reporting.
 *
 * @param {Object} editData - The object to normalize and report SNAKE CASE KEYS
 * @param {Object} prevData - The previous object to compare against CAMEL CASE KEYS
 * @returns {Array} An array of objects with the field names formatted for analytics reporting.
 */
const updateAnalyticsFields = (editData, prevData) => Object.keys(editData).filter(key => key !== 'associated_resources' && editData[key] !== prevData[toCamelCase(key)]).map(key => {
  const newValue = editData[key] == null || editData[key] === undefined ? null : editData[key];
  return {
    field: key === 'booking_code_type' ? 'discount_type' : key,
    new_value: key === 'booking_code_type' ? `${newValue}`.toLowerCase() : `${newValue}`
  };
});

/**
 * Used for analytics when creating a new coupon.
 * Returns an array representation of an object (fields and values) with the field names
 * filtered and normalized to the expected analytics reporting format, coercing values
 * to strings. Some fields are excluded or renamed for analytics reporting.
 *
 * @param {Object} editData - The object to normalize and report SNAKE CASE KEYS
 * @returns {Array} An array of objects with the field names formatted for analytics reporting.
 */
const createAnalyticsFields = editData => Object.keys(editData).filter(key => key !== 'associated_resources' && editData[key] !== null && typeof editData[key] !== 'undefined').map(key => ({
  field: key === 'booking_code_type' ? 'discount_type' : key,
  new_value: key === 'booking_code_type' ? `${editData[key]}`.toLowerCase() : `${editData[key]}`
}));
const containerCss = "c1gvoujq";
const Container = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, props.className)} />;
});
const headerContainerCss = "h299d8x";
const HeaderContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(headerContainerCss, props.className)} />;
});
const headerLeftCss = "h1bzi1sg";
const HeaderLeft = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(headerLeftCss, props.className)} />;
});
const styledButtonCss = "s1g0c4cx";
const StyledButton = forwardRef((props, ref) => <Button ref={ref} {...props} className={classNames(styledButtonCss, props.className)} />);
const leftStyledButtonCss = "lbh2ow0";
const LeftStyledButton = forwardRef((props, ref) => <StyledButton ref={ref} {...props} className={classNames(leftStyledButtonCss, props.className)} />);
const couponsListCss = "c53ftdf";
const CouponsList = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(couponsListCss, props.className)} />;
});
const couponCardCss = "cqs15m3";
const CouponCard = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(couponCardCss, props.className)} />;
});
const couponLeftSectionCss = "c1mi316s";
const CouponLeftSection = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(couponLeftSectionCss, props.className)} />;
});
const couponCodeCss = "cid43zt";
const CouponCode = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(couponCodeCss, props.className)} />;
});
const codeTextCss = "c11l3ndt";
const CodeText = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(codeTextCss, props.className)} />;
});
const couponDetailsCss = "c1i2d7dp";
const CouponDetails = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(couponDetailsCss, props.className)} />;
});
const discountInfoCss = "d11rke6u";
const DiscountInfo = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(discountInfoCss, props.className)} />;
});
const couponRightSectionCss = "c1ggx2sy";
const CouponRightSection = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(couponRightSectionCss, props.className)} />;
});
const actionButtonsCss = "a1g6kn27";
const ActionButtons = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(actionButtonsCss, props.className)} />;
});
export const PaymentCoupons = forwardRef(({
  coupons = [],
  isLoading = false,
  error = null,
  onCreateCoupon,
  onUpdateCoupon,
  onDeleteCoupon,
  eventType,
  isCreating = false,
  isUpdating = false,
  isDeleting = false
}, ref) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [couponToEdit, setCouponToEdit] = useState(null);
  const [apiError, setApiError] = useState(null);
  const targetRef = useRef(null);
  const track = useTrack();
  const {
    values: formValues,
    validateForm
  } = useFormContext();
  const [updateEventType] = useUpdateEventTypeMutation({
    id: eventType.id
  });
  const currencySymbolMap = useMemo(() => {
    const map = new Map();
    if (eventType?.available_currencies) {
      eventType.available_currencies.forEach(currency => {
        map.set(currency.code, currency.symbol);
      });
    }
    return map;
  }, [eventType?.available_currencies]);
  useImperativeHandle(ref, () => ({
    closeModal: () => {
      if (isModalOpen) {
        setIsModalOpen(false);
        setCouponToEdit(null);
        setApiError(null);
      }
    }
  }), [isModalOpen]);

  // Reusable function to update payment method attributes
  const updatePaymentMethodAttributes = async () => {
    if (!eventType?.id) return true;
    try {
      const {
        paymentTerms,
        amount
      } = formValues;
      const formErrors = await validateForm();
      if (formErrors && Object.keys(formErrors).length > 0) {
        // Don't proceed if there are any validation errors
        return false;
      }

      // Check if any relevant fields have changed
      const originalPaymentMethod = eventType.payment_method || {};
      const originalAmount = Number(originalPaymentMethod.amount_cents || 0) / 100;
      const serverProvider = resolvePaymentProviderForServer(formValues.provider, eventType);
      const hasChanges = serverProvider !== originalPaymentMethod.provider || amount !== originalAmount || formValues.currency !== originalPaymentMethod.currency;
      if (hasChanges) {
        updateEventType({
          eventType,
          attributes: {
            payment_method_attributes: {
              id: formValues.paymentId,
              provider: serverProvider,
              currency: formValues.currency,
              amount,
              payment_terms: paymentTerms,
              enabled: true
            }
          }
        }).unwrap();
      }
      return true;
    } catch (updateError) {
      console.error('Failed to update payment method attributes:', updateError);
      return false;
    }
  };
  const handleOpenModal = async () => {
    const success = await updatePaymentMethodAttributes();
    if (success) {
      track({
        name: TRACKING_EVENT_NAMES.EVENT_TYPE_EDITOR_INTERACTION,
        properties: {
          event_type_uuid: eventType.uuid,
          section: 'payments',
          action: 'click_create_coupon_code'
        }
      });
      setCouponToEdit(null);
      setApiError(null);
      setIsModalOpen(true);
    }
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCouponToEdit(null);
    setApiError(null);
  };
  const handleEditCoupon = async coupon => {
    const success = await updatePaymentMethodAttributes();
    if (success) {
      track({
        name: TRACKING_EVENT_NAMES.BOOKING_CODE_MANAGEMENT,
        properties: {
          event_type_uuid: eventType.uuid,
          action: 'edit_booking_code',
          booking_code_uuid: coupon.uuid
        }
      });
      setCouponToEdit(coupon);
      setApiError(null);
      setIsModalOpen(true);
    }
  };
  const handleDeleteCoupon = async couponUuid => {
    try {
      await onDeleteCoupon(couponUuid).unwrap();

      // Close modal if the deleted coupon is currently being edited
      if (couponToEdit && couponToEdit.uuid === couponUuid) {
        handleCloseModal();
      }
      track({
        name: TRACKING_EVENT_NAMES.BOOKING_CODE_MANAGEMENT,
        properties: {
          event_type_uuid: eventType.uuid,
          action: 'delete_booking_code',
          booking_code_uuid: couponUuid
        }
      });
    } catch (deleteError) {
      console.error('Failed to delete booking code:', deleteError);
    }
  };
  const handleSubmit = async (values, couponCurrency, isEditMode, editingCoupon) => {
    try {
      const apiData = {
        code: values.code.toUpperCase(),
        description: values.name,
        booking_code_type: values.type === 'percentage' ? 'PERCENT' : 'FIXED',
        currency: couponCurrency,
        active: true,
        associated_resources: [{
          resource_uuid: eventType.uuid,
          resource_type: 'EventType'
        }]
      };
      if (values.type === 'percentage') {
        apiData.percent_off = values.amount; // API expects percentage as whole number
      } else {
        apiData.fixed_amount_cents = Math.round(values.amount * 100); // Convert to cents
      }

      // Add optional fields
      if (values.firstTimeOnly) {
        apiData.max_per_invitee = 1;
      } else {
        apiData.max_per_invitee = isEditMode ? null : undefined;
      }
      if (values.limitUsage && values.usageLimit) {
        apiData.max_redemptions = parseInt(values.usageLimit, 10);
      } else {
        apiData.max_redemptions = isEditMode ? null : undefined;
      }
      if (values.addExpiration && values.expirationDate && values.expirationTime) {
        const expirationDateTime = parseExpirationDateTime(values.expirationDate, values.expirationTime);
        if (expirationDateTime) {
          apiData.end_at = expirationDateTime.toISO();
        }
      } else {
        apiData.end_at = isEditMode ? null : undefined;
      }
      if (isEditMode && editingCoupon) {
        await onUpdateCoupon({
          uuid: editingCoupon.uuid,
          data: apiData
        }).unwrap();
        track({
          name: TRACKING_EVENT_NAMES.BOOKING_CODE_MANAGEMENT,
          properties: {
            action: 'booking_code_updated',
            event_type_uuid: eventType.uuid,
            booking_code_uuid: editingCoupon.uuid,
            fields_updated: updateAnalyticsFields(apiData, editingCoupon)
          }
        });
      } else {
        const response = await onCreateCoupon(apiData).unwrap();
        if (response.resource && typeof response.resource === 'object') {
          track({
            name: TRACKING_EVENT_NAMES.BOOKING_CODE_MANAGEMENT,
            properties: {
              action: 'booking_code_created',
              booking_code_uuid: response.resource.uuid,
              event_type_uuid: eventType.uuid,
              fields_updated: createAnalyticsFields(apiData)
            }
          });
        }
      }
      setApiError(null);
      handleCloseModal();
    } catch (submitError) {
      if (submitError?.type === 'validation' && submitError.payload?.details) {
        const codeError = submitError.payload.details.find(detail => detail.parameter === 'code');
        const dateError = submitError.payload.details.find(detail => detail.parameter === 'end_at');
        if (codeError) {
          setApiError({
            field: 'code',
            message: codeError.message
          });
        } else if (dateError) {
          setApiError({
            field: 'expirationTime',
            message: dateError.message
          });
        } else {
          // If no specific code error, show general message
          setApiError({
            field: 'code',
            message: submitError.payload.message || I18n.t('publisher.event_types.payment_method.validation_error')
          });
        }
      } else if (submitError?.message) {
        setApiError({
          field: 'code',
          message: submitError.message
        });
      } else {
        setApiError({
          field: 'code',
          message: I18n.t('publisher.event_types.payment_method.unexpected_error')
        });
      }
      console.error('Failed to create/update booking code:', submitError);
    }
  };
  const formatDiscountInfo = coupon => {
    let info = '';
    // Handle percentage discounts
    if (coupon.bookingCodeType === 'PERCENT' && coupon.percentOff != null) {
      info = `${coupon.percentOff}% ${I18n.t('publisher.event_types.payment_method.off')}`;
    } else if (coupon.bookingCodeType === 'FIXED' && coupon.fixedAmountCents != null) {
      // Handle fixed amount discounts
      const offText = I18n.t('publisher.event_types.payment_method.off');
      const currencyCode = coupon.currency || 'USD';
      const currencySymbol = currencySymbolMap.get(currencyCode) || '$';
      info = `${currencySymbol}${(coupon.fixedAmountCents / 100).toFixed(2)} ${offText} (${currencyCode})`;
    } else {
      // Fallback
      info = I18n.t('publisher.event_types.payment_method.discount_available');
    }

    // Add expiration info
    if (coupon.endAt) {
      const formattedDate = formatExpirationDate(coupon.endAt);
      if (formattedDate) {
        const expiresText = I18n.t('publisher.event_types.payment_method.expires');
        info += `, ${expiresText} ${formattedDate}`;
      }
    }
    return info;
  };
  const noCoupons = isLoading || error || !coupons || coupons.length === 0;
  const renderCoupons = () => {
    if (noCoupons) {
      return null;
    }
    return <CouponsList>
        {coupons.map(coupon => <CouponCard key={coupon.uuid}>
            <CouponLeftSection>
              <CouponCode>
                <CodeText>{coupon.code}</CodeText>
              </CouponCode>
              <CouponDetails>
                <DiscountInfo>{formatDiscountInfo(coupon)}</DiscountInfo>
              </CouponDetails>
            </CouponLeftSection>

            <CouponRightSection>
              <ActionButtons>
                <IconButton title={I18n.t('publisher.event_types.payment_method.edit_coupon')} onClick={() => handleEditCoupon(coupon)} ref={targetRef}>
                  <EditPencilIcon />
                </IconButton>
                <IconButton title={I18n.t('publisher.event_types.payment_method.delete_coupon')} onClick={() => handleDeleteCoupon(coupon.uuid)} disabled={isDeleting}>
                  <CloseIcon />
                </IconButton>
              </ActionButtons>
            </CouponRightSection>
          </CouponCard>)}
      </CouponsList>;
  };
  return <Container>
      {coupons && coupons.length > 0 ? <HeaderContainer>
          <HeaderLeft>
            <BodyText weight="bold">{I18n.t('publisher.event_types.payment_method.coupon_codes_label')}</BodyText>
          </HeaderLeft>
          <StyledButton decoration="ghost" onClick={handleOpenModal} ref={targetRef}>
            <AddIcon width={12} />
            {I18n.t('publisher.event_types.payment_method.create_coupon_code')}
          </StyledButton>
        </HeaderContainer> : <>
          <BodyText weight="bold">{I18n.t('publisher.event_types.payment_method.coupon_codes_label')}</BodyText>
          <LeftStyledButton decoration="ghost" onClick={handleOpenModal} ref={targetRef}>
            <AddIcon width={12} />
            {I18n.t('publisher.event_types.payment_method.create_coupon_code')}
          </LeftStyledButton>
        </>}

      {renderCoupons()}

      <CouponModal isOpen={isModalOpen} onClose={handleCloseModal} onSubmit={handleSubmit} isLoading={isCreating || isUpdating} isEditMode={!!couponToEdit} couponToEdit={couponToEdit} targetRef={targetRef} apiError={apiError} />
    </Container>;
});

require("./payment_coupons.wyw-in-js.css!=!../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./payment_coupons.js");